import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import PageWelcomeHeading from '../PageWelcomeHeading'
import AssetGroups from './AssetGroups'
import GoogleCampaignSection from './GoogleCampaignSection'
import LineItemsList from '../LineItemsList'
import AdsList from '../../AdsList'
import SummaryGraphs from '../../../../features/components/SummaryGraphs'
import AdCopyPreviewsButton from '../../../../features/components/Buttons/AdCopyPreviewsButton'

import { clearUpdateCampaign } from '../../../../modules/actions/campaigns'
import {
  campaignSelector,
  campaignUpdateErrorSelector,
  campaignWasLoadedSelector,
  campaignWasUpdatedSelector
} from '../../../../modules/selectors/campaigns'
import {
  lineItemsErrorSelector,
  lineItemsSelector,
  lineItemsWasLoadedSelector
} from '../../../../modules/selectors/lineItems'
import {
  assetGroupsErrorSelector,
  assetGroupsSelector,
  assetGroupsWasLoadedSelector
} from '../../../../modules/selectors/assets'
import { adsSelector, adsIsLoadingSelector } from '../../../../modules/selectors/ads'

import { CAMPAIGN_GOOGLE_PERFORMANCE_MAX_TYPE } from '../../../../constants/campaigns'

function GoogleCampaignsSummaryContent({
  campaignName,
  DateRangeLabel,
  adsSectionRef,
  scrollToAdsSectionHandler,
  scrollToReportsSectionHandler,
  reusableSummaryGraphsProps
}) {
  const dispatch = useDispatch()

  const campaign = useSelector(campaignSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const lineItems = useSelector(lineItemsSelector)
  const lineItemsError = useSelector(lineItemsErrorSelector)
  const lineItemsWasLoaded = useSelector(lineItemsWasLoadedSelector)
  const assetGroups = useSelector(assetGroupsSelector)
  const assetGroupsError = useSelector(assetGroupsErrorSelector)
  const assetGroupsWasLoaded = useSelector(assetGroupsWasLoadedSelector)
  const ads = useSelector(adsSelector)
  const adsIsLoading = useSelector(adsIsLoadingSelector)
  const campaignUpdateWasUpdated = useSelector(campaignWasUpdatedSelector)
  const campaignUpdateError = useSelector(campaignUpdateErrorSelector)

  const { advertising_channel_type: advertisingChanelType } = campaign

  const lineItemsEmpty = !lineItems.length
  const assetGroupsEmpty = !assetGroups.length
  const adsEmpty = !ads.length

  const lineItemsLoaded = lineItemsWasLoaded || lineItemsError
  const assetGroupsLoaded = assetGroupsWasLoaded || assetGroupsError

  const isPerformanceMaxCampaign = advertisingChanelType === CAMPAIGN_GOOGLE_PERFORMANCE_MAX_TYPE

  // for performance max campaign we render just AssetGroups
  // for other types of campaigns we render usual structure line items > ads > reports (not implemented)
  const isPageDataLoading = !campaignWasLoaded || (!isPerformanceMaxCampaign && (!lineItemsLoaded || adsIsLoading))

  const showGoogleCampaignSection = !campaignWasLoaded || isPerformanceMaxCampaign

  useEffect(() => {
    if (campaignUpdateWasUpdated || campaignUpdateError) {
      dispatch(clearUpdateCampaign())
    }
  }, [dispatch, campaignUpdateWasUpdated, campaignUpdateError])

  return (
    <>
      <PageWelcomeHeading
        isPageDataLoading={isPageDataLoading}
        campaignName={campaignName}
        showButtons={!isPerformanceMaxCampaign}
        scrollToAdsSectionHandler={scrollToAdsSectionHandler}
        scrollToReportsSectionHandler={scrollToReportsSectionHandler}
      />
      {showGoogleCampaignSection && <GoogleCampaignSection />}

      {campaignWasLoaded &&
        (isPerformanceMaxCampaign ? (
          <>
            <AssetGroups />
            {assetGroupsLoaded && !assetGroupsEmpty && (
              // render report after we know that assetGroups was fetched
              <SummaryGraphs {...reusableSummaryGraphsProps} />
            )}
          </>
        ) : (
          <>
            <LineItemsList title="List of Ad Groups" />
            {lineItemsLoaded && !lineItemsEmpty && (
              // render ads and report after we know that lineItems was fetched
              // to prevent flicking these sections with "no data" text
              <>
                <AdsList
                  scrollRef={adsSectionRef}
                  RightPanel={
                    <>
                      DateRangeLabel
                      <AdCopyPreviewsButton />
                    </>
                  }
                />
                <SummaryGraphs hideGraphsSection={adsEmpty} {...reusableSummaryGraphsProps} />
              </>
            )}
          </>
        ))}
    </>
  )
}

GoogleCampaignsSummaryContent.propTypes = {
  DateRangeLabel: PropTypes.node,
  adsSectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  scrollToAdsSectionHandler: PropTypes.func.isRequired,
  scrollToReportsSectionHandler: PropTypes.func.isRequired,
  reusableSummaryGraphsProps: PropTypes.object.isRequired
}

export default GoogleCampaignsSummaryContent
